import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  TextField,
  CircularProgress,
  Button as MuiButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { BlockButtonsTab } from './BlockButtonsTab';
import { RichTextEditor } from '../components/RichTextEditor';
import ImagePicker from '../components/ImagePicker';
import { ButtonCreateModal } from './ButtonCreateModal';
import { sanitizeTelegramHtml } from '../utils';
import { ButtonItem, ButtonsToBlocksItem } from './interface';
import { ExtraFieldsTab, ExtraField } from '../components/ExtraFieldsTab';

interface BlockModalProps {
  blockId: number | null;
  open: boolean;
  onClose: () => void;
  onUpdateTree?: () => void;
}

const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;
const API_URL = import.meta.env.VITE_API_URL;

type TabKey = 'main' | 'buttons' | 'preview' | 'extra';

export function BlockModal({
  blockId,
  open,
  onClose,
  onUpdateTree,
}: BlockModalProps) {
  const [tab, setTab] = useState<TabKey>('main');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [block, setBlock] = useState<any>(null);
  const [allBlocks, setAllBlocks] = useState<any[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [mediaId, setMediaId] = useState<number | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [defaultButtons, setDefaultButtons] = useState<ButtonItem[][]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [extraFields, setExtraFields] = useState<ExtraField[]>([]);

  useEffect(() => {
    if (open) setTab('main');
  }, [open, blockId]);

  useEffect(() => {
    if (!blockId || !open) return;
    setLoading(true);
    fetch(`${API_URL}/content/${blockId}`)
      .then((res) => res.json())
      .then((data) => {
        const b = data.data;
        setBlock({
          ...b,
          parent_id: b.parent_id ?? b.parentId ?? null,
          keyboardType: b.keyboardType ?? 'inline',
        });
      })
      .finally(() => setLoading(false));
  }, [blockId, open]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/blocks`)
      .then((res) => res.json())
      .then((data) =>
        setAllBlocks(
          data.data?.filter((b: any) => b.block_id !== blockId) || [],
        ),
      );
  }, [open, blockId]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/default-buttons`)
      .then((res) => res.json())
      .then((data) => setDefaultButtons(data.data || []));
  }, [open]);

  useEffect(() => {
    if (!block) return;
    setMediaId(block.mediaId ?? null);
    setImageUrl(
      block?.contentMedia
        ? `${IMAGE_URL}/${block?.contentMedia?.imageUrl}`
        : null,
    );
    setImage(null);
  }, [block]);

  useEffect(() => {
    if (!block) return;
    setExtraFields(block.extraFields ?? []);
  }, [block]);

  const setText = (html: string) => {
    setBlock((prev: any) => ({ ...prev, text: html }));
  };

  const validate = () => {
    const newErrors: string[] = [];

    if (!block.label?.trim()) newErrors.push('Назва не може бути порожньою');
    if (!block.text?.trim()) newErrors.push('Текст не може бути порожнім');

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = async () => {
    if (!blockId || !block) return;
    setErrors([]);

    if (!validate()) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('label', block.label);
      formData.append('text', sanitizeTelegramHtml(block.text || ''));
      formData.append('parentId', block.parent_id ?? '');
      formData.append('keyboardType', block.keyboardType || 'inline');

      if (image) formData.append('image', image);
      if (mediaId) formData.append('mediaId', String(mediaId));

      const res = await fetch(`${API_URL}/content/${blockId}`, {
        method: 'PUT',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        if (Array.isArray(data.error)) {
          setErrors(data.error);
        } else {
          setErrors([data.error]);
        }
        return;
      }

      setBlock(data);
      onClose();
      if (onUpdateTree) onUpdateTree();
    } finally {
      setSaving(false);
    }
  };

  const handleAddDefaultButton = async (
    btn: ButtonItem,
    defaultType?: 'back_to_main_menu' | 'back',
  ) => {
    if (!block) return;
    let payload: any = {
      blockId: block.blockId,
      label: btn.label,
      type: btn.type,
      url: btn.url,
      callback: btn.callback,
      nextBlockId: btn.nextBlockId,
      isFullWidth: true,
      rowOrder: (block.ButtonsToBlocks?.length ?? 0) + 1,
      orderX: null,
      orderY: null,
    };

    if (defaultType === 'back_to_main_menu') {
      payload.label = '🏠 Повернутись до головного меню';
      payload.type = 'callback';
      payload.nextBlockId = 1;
      payload.callback = 'main_menu';
      payload.defaultType = defaultType;
    } else if (defaultType === 'back') {
      payload.label = '🔙 Повернутись назад';
      payload.type = 'callback';
      payload.nextBlockId = block.parent_id ?? 1;
      payload.callback = 'back';
      payload.defaultType = defaultType;
    }

    try {
      const res = await fetch(`${API_URL}/content/default-button`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const created = await res.json();
      setBlock((prev: any) => ({
        ...prev,
        ButtonsToBlocks: [...(prev.ButtonsToBlocks || []), created],
      }));
    } catch (err) {
      console.error('Failed to add default button:', err);
    }
  };

  const renderPreviewButtons = (buttons: ButtonsToBlocksItem[]) => {
    if (!buttons?.length) return null;

    if (block.keyboardType === 'reply') {
      return (
        <Box mt={2}>
          <Box
            sx={{
              border: '1px solid #ddd',
              borderRadius: 2,
              p: 2,
              background: '#f9f9f9',
            }}
          >
            {buttons.map((b) => (
              <MuiButton
                key={b.id}
                variant="contained"
                fullWidth
                sx={{ mb: 1 }}
              >
                {b.Button.label}
              </MuiButton>
            ))}
          </Box>
        </Box>
      );
    }

    const fullWidth = buttons
      .filter((b) => b.isFullWidth)
      .sort((a, b) => (a.rowOrder ?? 0) - (b.rowOrder ?? 0));

    const normalButtons = buttons.filter((b) => !b.isFullWidth);
    const rows: Record<number, ButtonsToBlocksItem[]> = {};

    normalButtons.forEach((b) => {
      const row = b.orderY ?? 0;
      if (!rows[row]) rows[row] = [];
      rows[row].push(b);
    });

    Object.keys(rows).forEach((row) => {
      rows[Number(row)].sort((a, b) => (a.orderX ?? 0) - (b.orderX ?? 0));
    });

    return (
      <Box display="flex" flexDirection="column" gap={1} mt={2}>
        {fullWidth.map((b) => (
          <MuiButton key={b.id} variant="contained" fullWidth>
            {b.Button.label}
          </MuiButton>
        ))}

        {Object.keys(rows)
          .sort((a, b) => Number(a) - Number(b))
          .map((rowKey) => (
            <Box key={rowKey} display="flex" gap={1}>
              {rows[Number(rowKey)].map((b) => (
                <MuiButton key={b.id} variant="contained">
                  {b.Button.label}
                </MuiButton>
              ))}
            </Box>
          ))}
      </Box>
    );
  };

  const showBtnSettings = !!block?.showBtnSettings;
  const hasExtraFields = (block?.extraFields?.length ?? 0) > 0;

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => {
        onClose();
        if (onUpdateTree) onUpdateTree();
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {block?.label}
        <IconButton edge="end" color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab value="main" label="Основне" />

        {showBtnSettings && <Tab value="buttons" label="Кнопки" />}

        {showBtnSettings && <Tab value="preview" label="Превью" />}

        {hasExtraFields && <Tab value="extra" label="Додаткові налаштування" />}
      </Tabs>

      <DialogContent sx={{ minHeight: 500 }}>
        {loading && <CircularProgress />}

        {!loading && block && (
          <>
            {tab === 'main' && (
              <Box display="flex" flexDirection="column" gap={2}>
                {errors.length > 0 && (
                  <Box
                    sx={{
                      backgroundColor: '#fdecea',
                      color: '#b71c1c',
                      border: '1px solid #f5c6cb',
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </Box>
                )}

                <TextField
                  label="Назва"
                  fullWidth
                  value={block.label || ''}
                  onChange={(e) =>
                    setBlock((prev: any) => ({
                      ...prev,
                      label: e.target.value,
                    }))
                  }
                />

                {block.blockId !== 1 && (
                  <FormControl fullWidth>
                    <InputLabel>Батьківський блок</InputLabel>
                    <Select
                      value={block.parent_id ?? ''}
                      onChange={(e) =>
                        setBlock((prev: any) => ({
                          ...prev,
                          parent_id:
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                        }))
                      }
                    >
                      <MenuItem value="">
                        <em>Без батьківського</em>
                      </MenuItem>

                      {allBlocks.map((b) => (
                        <MenuItem key={b.block_id} value={b.block_id}>
                          {b.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Box display="flex" gap={2}>
                  <Box
                    onClick={() =>
                      setBlock((prev: any) => ({
                        ...prev,
                        keyboardType: 'inline',
                      }))
                    }
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      border:
                        block.keyboardType === 'inline'
                          ? '2px solid #1976d2'
                          : '1px solid #ccc',
                      p: 2,
                      width: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>Клавіатура під повідомленням</Box>
                  </Box>

                  <Box
                    onClick={() =>
                      setBlock((prev: any) => ({
                        ...prev,
                        keyboardType: 'reply',
                      }))
                    }
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      border:
                        block.keyboardType === 'reply'
                          ? '2px solid #1976d2'
                          : '1px solid #ccc',
                      p: 2,
                      width: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>Клавіатура під текстовим полем користувача</Box>
                  </Box>
                </Box>

                {block.keyboardType === 'reply' && (
                  <Box sx={{ fontSize: 12, fontStyle: 'italic' }}>
                    Системні кнопки не працюють в цьому режимі
                  </Box>
                )}

                <ImagePicker
                  title="Зображення"
                  value={imageUrl || ''}
                  onChange={(value) => {
                    if (value.type === 'file') {
                      setImage(value.file);
                      setMediaId(null);
                      setImageUrl(URL.createObjectURL(value.file));
                    }
                    if (value.type === 'media') {
                      setImage(null);
                      setMediaId(value.mediaId);
                      setImageUrl(value.url);
                    }
                  }}
                />

                <RichTextEditor
                  label="Текст"
                  minRows={4}
                  value={block.text || ''}
                  onChange={setText}
                />

                <MuiButton
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Збереження...' : 'Зберегти'}
                </MuiButton>
              </Box>
            )}

            {tab === 'buttons' && showBtnSettings && (
              <Box display="flex" flexDirection="column" gap={2}>
                <MuiButton
                  variant="outlined"
                  onClick={() => setCreateModalOpen(true)}
                >
                  Додати кнопку
                </MuiButton>

                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {defaultButtons.flat().map((btn) => {
                    const exists = block.ButtonsToBlocks?.some(
                      (b: ButtonsToBlocksItem) =>
                        b.Button.type === 'programmed' &&
                        b.Button.buttonId === btn.buttonId,
                    );
                    if (exists) return null;

                    return (
                      <MuiButton
                        key={btn.buttonId}
                        variant="outlined"
                        sx={{ borderRadius: '50px' }}
                        onClick={() => handleAddDefaultButton(btn)}
                      >
                        {btn.label}
                      </MuiButton>
                    );
                  })}
                </Box>

                <BlockButtonsTab
                  block={block}
                  onChange={(updatedButtons) =>
                    setBlock((prev: any) => ({
                      ...prev,
                      ButtonsToBlocks: updatedButtons,
                    }))
                  }
                />

                <ButtonCreateModal
                  blockId={block.blockId}
                  open={createModalOpen}
                  onClose={() => setCreateModalOpen(false)}
                  onCreated={(item) => {
                    setBlock((prev: any) => ({
                      ...prev,
                      ButtonsToBlocks: [...(prev.ButtonsToBlocks || []), item],
                    }));
                    setCreateModalOpen(false);
                  }}
                />
              </Box>
            )}

            {tab === 'preview' && (
              <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2 }}>
                {(imageUrl || block.mediaId) && (
                  <img
                    src={
                      imageUrl || `${IMAGE_URL}/${block.contentMedia?.imageUrl}`
                    }
                    style={{ maxWidth: '100%', borderRadius: 8 }}
                  />
                )}

                <Box dangerouslySetInnerHTML={{ __html: block?.text || '' }} />

                {block.ButtonsToBlocks &&
                  renderPreviewButtons(block.ButtonsToBlocks)}
              </Box>
            )}

            {tab === 'extra' && hasExtraFields && (
              <ExtraFieldsTab fields={extraFields} onChange={setExtraFields} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
